export const VEDIC_DAY_COLORS = [
  'linear-gradient(135deg, #F59E0B, #EF4444)',
  'linear-gradient(135deg, #EF4444, #EC4899)',
  'linear-gradient(135deg, #EC4899, #A855F7)',
  'linear-gradient(135deg, #A855F7, #06B6D4)',
  'linear-gradient(135deg, #06B6D4, #10B981)',
];

export const VEDIC_DAYS = [
  {
    day: 1,
    weekday: { en: 'Monday', ja: '月曜日' },
    theme: { en: 'The Magic of Numbers ✨', ja: '数字の魔法 ✨' },
    goal: {
      en: 'Discover how ancient Indian mathematicians found shortcuts that make math fast and fun!',
      ja: '古代インドの数学者が見つけた、数学を速く楽しくするショートカットを発見しよう！',
    },
    vocab: [
      { word: { en: 'Vedic', ja: 'Vedic' }, def: { en: 'From ancient Indian knowledge (Vedas)', ja: 'インドの古代知識（ヴェーダ）から' }, emoji: '🕉️' },
      { word: { en: 'Sutra', ja: 'Sutra' }, def: { en: 'A rule or formula in Sanskrit', ja: 'サンスクリット語の公式やルール' }, emoji: '📜' },
      { word: { en: 'Digit', ja: 'Digit' }, def: { en: 'A single number 0–9', ja: '0〜9の1つの数字' }, emoji: '🔢' },
      { word: { en: 'Complement', ja: 'Complement' }, def: { en: 'Numbers that add up to 10', ja: '合計が10になる数' }, emoji: '➕' },
      { word: { en: 'Pattern', ja: 'Pattern' }, def: { en: 'A repeating rule in numbers', ja: '数字の中の繰り返しのルール' }, emoji: '🔄' },
    ],
    morning: {
      time: '10:30–12:00',
      title: { en: 'Welcome to Vedic Math!', ja: 'ヴェーダ数学へようこそ！' },
      activities: [
        {
          time: '10:30',
          duration: 15,
          title: { en: 'Number Magic Warm-up', ja: 'ナンバーマジックウォームアップ' },
          type: 'icebreaker',
          emoji: '🎩',
          content: {
            en: 'Teacher does "mind-reading" number tricks. Students try to figure out the secret. Reveal: it\'s all patterns!',
            ja: '先生が「数字の読心術」マジックを披露。生徒は秘密を探る。答え：全てパターン！',
          },
        },
        {
          time: '10:45',
          duration: 30,
          title: { en: 'What is Vedic Math? History & Wow Facts', ja: 'ヴェーダ数学とは？歴史と驚きの事実' },
          type: 'lesson',
          emoji: '📖',
          content: {
            en: 'Vedic Math comes from ancient India (1500+ years ago). 16 Sutras (word formulas) that make all calculations faster. Example: multiplying by 11 in 2 seconds!',
            ja: 'ヴェーダ数学は古代インド（1500年以上前）から。16のスートラ（言葉の公式）で全ての計算が速くなる。例：11倍算を2秒で！',
          },
        },
        {
          time: '11:15',
          duration: 45,
          title: { en: 'Sutra #1 — "All from 9, last from 10" (Complements)', ja: 'スートラ#1 — 「9から全て、10から最後」（補数）' },
          type: 'activity',
          emoji: '🔢',
          content: {
            en: 'Learn complements of 10 and 9. Practice: 9 complements race. Subtraction becomes addition! 100 − 37 = ? Just use complement of 37 from 100.',
            ja: '10と9の補数を学ぶ。練習：補数レース。引き算が足し算になる！100 − 37 = ? 37の補数を使うだけ！',
          },
        },
      ],
    },
    afternoon: { time: '', activities: [] },
  },
  {
    day: 2,
    weekday: { en: 'Tuesday', ja: '火曜日' },
    theme: { en: 'Lightning Fast Addition & Subtraction ⚡', ja: '超高速たし算・ひき算 ⚡' },
    goal: {
      en: 'Master mental addition and subtraction using Vedic grouping and base methods.',
      ja: 'ヴェーダのグループ化とベース法で暗算の足し算・引き算をマスターしよう。',
    },
    vocab: [
      { word: { en: 'Base', ja: 'Base' }, def: { en: 'A reference number like 10, 100', ja: '10や100のような基準の数' }, emoji: '🎯' },
      { word: { en: 'Deviation', ja: 'Deviation' }, def: { en: 'How far a number is from the base', ja: '基準の数からの距離' }, emoji: '↔️' },
      { word: { en: 'Carry', ja: 'Carry' }, def: { en: 'Moving a digit to the next column', ja: '次の桁に数を繰り越すこと' }, emoji: '➡️' },
      { word: { en: 'Mental math', ja: 'Mental math' }, def: { en: 'Calculating in your head', ja: '頭の中で計算すること' }, emoji: '🧠' },
      { word: { en: 'Grouping', ja: 'Grouping' }, def: { en: 'Combining numbers to make easy pairs', ja: '計算しやすいペアに数をまとめること' }, emoji: '🤝' },
    ],
    morning: {
      time: '10:30–12:00',
      title: { en: 'Left-to-Right Addition & The 10-Group Trick', ja: '左から右への足し算と10グループトリック' },
      activities: [
        {
          time: '10:30',
          duration: 15,
          title: { en: 'Warm-up: Number Bond Relay', ja: 'ウォームアップ：ナンバーボンドリレー' },
          type: 'icebreaker',
          emoji: '🤝',
          content: {
            en: 'Students stand in a circle. Teacher calls a number — the next student says its complement to 100. Fast pace! Energy booster.',
            ja: '生徒が円になる。先生が数字を言う — 次の生徒が100との補数を言う。高速で！エネルギーアップ。',
          },
        },
        {
          time: '10:45',
          duration: 45,
          title: { en: 'Vedic Left-to-Right Addition', ja: 'ヴェーダの左から右への足し算' },
          type: 'lesson',
          emoji: '➕',
          content: {
            en: 'Normal math goes right-to-left. Vedic math goes LEFT-TO-RIGHT (like reading!). Example: 347 + 286. First: 3+2=5 (hundreds). Then 4+8=12 (adjust: 6 hundreds + 2 tens). Then 7+6=13. Result: 633. Practice with partner.',
            ja: '普通の算数は右から左。ヴェーダ数学は左から右（読むように）！例：347 + 286。まず：3+2=5（百の位）。次4+8=12（調整：6百+2十）。次7+6=13。結果：633。ペアで練習。',
          },
        },
        {
          time: '11:30',
          duration: 30,
          title: { en: 'Grouping to Make Tens', ja: '10を作るグルーピング' },
          type: 'activity',
          emoji: '🎯',
          content: {
            en: 'Given a list of numbers, spot pairs that make 10 first, then add the rest. Example: 7+8+3+2+6 = (7+3)+(8+2)+6 = 10+10+6 = 26. Card game: collect pairs that sum to 10.',
            ja: '数のリストから、まず10になるペアを見つけて足す。例：7+8+3+2+6 = (7+3)+(8+2)+6 = 10+10+6 = 26。カードゲーム：合計10になるペアを集める。',
          },
        },
      ],
    },
    afternoon: { time: '', activities: [] },
  },
  {
    day: 3,
    weekday: { en: 'Wednesday', ja: '水曜日' },
    theme: { en: 'Multiplication Superpowers 🦸', ja: '掛け算スーパーパワー 🦸' },
    goal: {
      en: 'Learn 3 powerful Vedic multiplication techniques: near base, criss-cross, and squaring.',
      ja: '3つの強力なヴェーダ掛け算テクニックを学ぶ：ベース法、クリスクロス法、2乗法。',
    },
    vocab: [
      { word: { en: 'Multiplier', ja: 'Multiplier' }, def: { en: 'The number you multiply by', ja: '掛ける数' }, emoji: '✖️' },
      { word: { en: 'Product', ja: 'Product' }, def: { en: 'The answer when you multiply', ja: '掛け算の答え' }, emoji: '📦' },
      { word: { en: 'Square', ja: 'Square' }, def: { en: 'Multiplying a number by itself', ja: '数を自分自身で掛けること' }, emoji: '⬜' },
      { word: { en: 'Criss-cross', ja: 'Criss-cross' }, def: { en: 'A diagonal multiplication method', ja: '斜めに掛け算する方法' }, emoji: '✂️' },
      { word: { en: 'Near base', ja: 'Near base' }, def: { en: 'Close to 10, 100 or 1000', ja: '10、100、1000に近い数' }, emoji: '📍' },
    ],
    morning: {
      time: '10:30–12:00',
      title: { en: 'Near-Base Multiplication (Numbers Close to 100)', ja: 'ベース近傍掛け算（100に近い数）' },
      activities: [
        {
          time: '10:30',
          duration: 15,
          title: { en: 'Warm-up: Multiplication Bingo', ja: 'ウォームアップ：掛け算ビンゴ' },
          type: 'icebreaker',
          emoji: '🎱',
          content: {
            en: 'Bingo cards with products. Teacher calls out multiplication problems using Vedic tricks. First to get Bingo wins!',
            ja: '積が書かれたビンゴカード。先生がヴェーダトリックで掛け算問題を言う。最初にビンゴになった人の勝ち！',
          },
        },
        {
          time: '10:45',
          duration: 45,
          title: { en: 'Near-100 Method: 98 × 97 = ?', ja: 'ベース100法：98 × 97 = ?' },
          type: 'lesson',
          emoji: '💯',
          content: {
            en: 'Step 1: Deviations from 100: 98→(-2), 97→(-3). Step 2: Cross subtract: 98-3=95 or 97-2=95 (same!). Step 3: Multiply deviations: (-2)×(-3)=6. Answer: 9506. Try: 96×98, 103×105!',
            ja: 'ステップ1：100からの差：98→(-2)、97→(-3)。ステップ2：交差引き算：98-3=95（同じ！）。ステップ3：差を掛ける：(-2)×(-3)=6。答え：9506。試してみよう：96×98、103×105！',
          },
        },
        {
          time: '11:30',
          duration: 30,
          title: { en: 'Squaring Numbers Ending in 5', ja: '5で終わる数の2乗' },
          type: 'activity',
          emoji: '⬛',
          content: {
            en: '35² = ? Take 3, multiply by next number 4: 3×4=12. Attach 25: 1225! Any number ending in 5: multiply the tens digit by (tens+1), attach 25. Try 45², 75², 95².',
            ja: '35² = ? 3を取って次の数4と掛ける：3×4=12。25をつける：1225！5で終わる数：十の位×(十の位+1)、25をつける。45²、75²、95²を試してみよう。',
          },
        },
      ],
    },
    afternoon: { time: '', activities: [] },
  },
  {
    day: 4,
    weekday: { en: 'Thursday', ja: '木曜日' },
    theme: { en: 'Division & Fractions Made Easy 🍕', ja: '割り算と分数を簡単に 🍕' },
    goal: {
      en: 'Use Vedic flag method and fraction shortcuts to divide and compare fractions effortlessly.',
      ja: 'ヴェーダのフラグ法と分数ショートカットで、割り算と分数の比較を楽にこなそう。',
    },
    vocab: [
      { word: { en: 'Dividend', ja: 'Dividend' }, def: { en: 'The number being divided', ja: '割られる数' }, emoji: '➗' },
      { word: { en: 'Divisor', ja: 'Divisor' }, def: { en: 'The number you divide by', ja: '割る数' }, emoji: '🔑' },
      { word: { en: 'Quotient', ja: 'Quotient' }, def: { en: 'The result of division', ja: '割り算の答え' }, emoji: '🏁' },
      { word: { en: 'Remainder', ja: 'Remainder' }, def: { en: 'What is left over after dividing', ja: '割り算で残った数' }, emoji: '🪣' },
      { word: { en: 'Fraction', ja: 'Fraction' }, def: { en: 'Part of a whole number', ja: '整数の一部' }, emoji: '🍕' },
    ],
    morning: {
      time: '10:30–12:00',
      title: { en: 'Vedic Division — The Flag Method', ja: 'ヴェーダ割り算 — フラグ法' },
      activities: [
        {
          time: '10:30',
          duration: 15,
          title: { en: 'Warm-up: Division Relay Race', ja: 'ウォームアップ：割り算リレー' },
          type: 'icebreaker',
          emoji: '🏃',
          content: {
            en: 'Teams of 4. Each person solves one step of a long division using Vedic method and passes to next. Fastest team wins!',
            ja: '4人チーム。ヴェーダ法を使って長い割り算を1ステップずつ解いて次の人に渡す。最速チームの勝ち！',
          },
        },
        {
          time: '10:45',
          duration: 45,
          title: { en: 'Dividing by 9 — The Remainder Shortcut', ja: '9で割る — 余りのショートカット' },
          type: 'lesson',
          emoji: '➗',
          content: {
            en: '43 ÷ 9: Quotient = 4 (first digit). Remainder = 4+3 = 7. Answer: 4 remainder 7! Check: 4×9+7=43 ✓. Try 52÷9, 73÷9, 81÷9. Discover the pattern!',
            ja: '43 ÷ 9：商 = 4（最初の数字）。余り = 4+3 = 7。答え：商4余り7！確認：4×9+7=43 ✓。52÷9、73÷9、81÷9を試そう。パターンを発見！',
          },
        },
        {
          time: '11:30',
          duration: 30,
          title: { en: 'Fractions — Cross-Multiplication Comparison', ja: '分数 — 交差掛け算比較' },
          type: 'activity',
          emoji: '🍕',
          content: {
            en: 'Which is bigger: 3/7 or 4/9? Cross multiply: 3×9=27, 4×7=28. Since 28>27, 4/9 > 3/7! Pizza sharing activity: who gets more?',
            ja: 'どちらが大きい：3/7か4/9？交差掛け算：3×9=27、4×7=28。28>27なので4/9 > 3/7！ピザ分けアクティビティ：誰が多く貰える？',
          },
        },
      ],
    },
    afternoon: { time: '', activities: [] },
  },
  {
    day: 5,
    weekday: { en: 'Friday', ja: '金曜日' },
    theme: { en: 'Vedic Math Showcase & Championship 🏆', ja: 'ヴェーダ数学ショーケース＆チャンピオンシップ 🏆' },
    goal: {
      en: 'Show off everything you have learned, teach your family, and celebrate becoming a Vedic Mathematician!',
      ja: '学んだことを全て披露し、家族に教えて、ヴェーダ数学者になったことを祝おう！',
    },
    vocab: [],
    morning: {
      time: '10:30–12:00',
      title: { en: 'Grand Review & Trick Showcase', ja: '総まとめとトリックショーケース' },
      activities: [
        {
          time: '10:30',
          duration: 20,
          title: { en: 'Warm-up: The Ultimate Speed Round', ja: 'ウォームアップ：究極スピードラウンド' },
          type: 'icebreaker',
          emoji: '⚡',
          content: {
            en: 'Flash through ALL tricks from Days 1-4. Each student picks their strongest trick to demonstrate to the class in 60 seconds.',
            ja: 'Day 1-4の全トリックを素早く復習。各生徒が得意なトリックを60秒でクラスに披露する。',
          },
        },
        {
          time: '10:50',
          duration: 40,
          title: { en: 'My Vedic Math Presentation (2 min each)', ja: '私のヴェーダ数学プレゼン（各2分）' },
          type: 'activity',
          emoji: '🎤',
          content: {
            en: 'Each student stands up and teaches ONE Vedic trick to the class: 1) Name the trick. 2) Show the method step by step. 3) Give an example. 4) Why do you love this trick?',
            ja: '各生徒が1つのヴェーダトリックをクラスに教える：1）トリックの名前。2）ステップごとに方法を見せる。3）例を示す。4）なぜこのトリックが好きか？',
          },
        },
        {
          time: '11:30',
          duration: 30,
          title: { en: 'Family Challenge Letter 📝', ja: 'ファミリーチャレンジレター 📝' },
          type: 'activity',
          emoji: '💌',
          content: {
            en: 'Write a letter/card to a family member teaching them ONE trick. Include: the trick name, how to do it, a practice problem, and your challenge for them to try it!',
            ja: '家族に1つのトリックを教える手紙/カードを書く。トリック名、やり方、練習問題、家族へのチャレンジを含める！',
          },
        },
      ],
    },
    afternoon: { time: '', activities: [] },
  },
];
